import React, { useState, useEffect } from 'react';

// Horaires MRG : Lun–Sam 9h00–13h00 & 14h00–19h00, dimanche fermé.
// minutes since midnight
const SCHEDULE = {
  1: [[540, 780], [840, 1140]],
  2: [[540, 780], [840, 1140]],
  3: [[540, 780], [840, 1140]],
  4: [[540, 780], [840, 1140]],
  5: [[540, 780], [840, 1140]],
  6: [[540, 780], [840, 1140]],
  0: [],
};
const DAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const fmt = (m) => `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
const localISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const frDate = (iso) => {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  } catch { return iso; }
};

export const computeStatus = (now, closures = []) => {
  const todayStr = localISO(now);
  const cl = (closures || []).find(c => c.from && c.to && todayStr >= c.from && todayStr <= c.to);
  if (cl) {
    const end = new Date(cl.to + 'T00:00:00'); end.setDate(end.getDate() + 1);
    return { open: false, holiday: true, label: `Fermé (congés) — réouverture le ${frDate(localISO(end))}` };
  }
  const cur = now.getHours() * 60 + now.getMinutes();
  const dow = now.getDay();
  for (const [o, c] of (SCHEDULE[dow] || [])) {
    if (cur >= o && cur < c) {
      const soon = c - cur <= 30;
      return { open: true, label: soon ? `Ferme bientôt · à ${fmt(c)}` : `Ouvert · ferme à ${fmt(c)}` };
    }
  }
  for (let off = 0; off <= 7; off++) {
    const d = (dow + off) % 7;
    for (const [o] of (SCHEDULE[d] || [])) {
      if (off === 0 && o <= cur) continue;
      const when = off === 0 ? "aujourd’hui" : (off === 1 ? 'demain' : DAYS[d]);
      return { open: false, label: `Fermé · ouvre ${when} à ${fmt(o)}` };
    }
  }
  return { open: false, label: 'Fermé' };
};

const DAYS_ORDER = [
  { idx: 1, label: 'Lundi' }, { idx: 2, label: 'Mardi' }, { idx: 3, label: 'Mercredi' },
  { idx: 4, label: 'Jeudi' }, { idx: 5, label: 'Vendredi' }, { idx: 6, label: 'Samedi' },
  { idx: 0, label: 'Dimanche' },
];
const fmtInterval = ([o, c]) => `${fmt(o)} – ${fmt(c)}`;

export const OpeningStatus = ({ closures = [], className = '', dark = false, accent = '#FF004D' }) => {
  const [now, setNow] = useState(new Date());
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);
  const st = computeStatus(now, closures);
  const color = st.open ? '#22c55e' : '#f43f5e';
  const todayIdx = now.getDay();
  return (
    <span className={`relative inline-flex ${className}`} data-testid="opening-status">
      <button type="button" onClick={() => setOpen(o => !o)} className="inline-flex items-center gap-1.5 cursor-pointer max-w-full" aria-expanded={open} data-testid="opening-status-btn">
        <span className="relative flex h-2 w-2 shrink-0">
          {st.open && <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-70" style={{ backgroundColor: color }} />}
          <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: color }} />
        </span>
        <span className={`${dark ? 'text-slate-700' : 'text-white/90'} truncate`}>
          <strong className="font-semibold">{st.open ? 'Ouvert' : 'Fermé'}</strong>
          <span className={`hidden sm:inline ${dark ? 'text-slate-400' : 'text-white/60'}`}> — {st.label.replace(/^(Ouvert|Fermé)\s[·—]\s?/, '')}</span>
        </span>
        <svg width="13" height="13" viewBox="0 0 24 24" className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="currentColor" style={{ color: accent }}><path d="M7 10l5 5 5-5z" /></svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[59]" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-2 w-[290px] max-w-[calc(100vw-1.5rem)] bg-white rounded-2xl shadow-2xl border border-slate-100 p-4 z-[60]" data-testid="opening-hours-panel">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[12px] font-bold uppercase tracking-wider" style={{ color: accent }}>Horaires d&apos;ouverture</p>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold" style={{ color }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} /> {st.open ? 'Ouvert' : 'Fermé'}
              </span>
            </div>
            <div className="divide-y divide-slate-50">
              {DAYS_ORDER.map(d => {
                const ints = SCHEDULE[d.idx] || [];
                const isToday = d.idx === todayIdx;
                return (
                  <div key={d.idx} className="flex items-start justify-between gap-4 py-2 text-[13px]"
                    style={isToday ? { color: accent, fontWeight: 600 } : { color: '#475569' }}>
                    <span className="flex items-center gap-2">
                      {isToday && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accent }} />}
                      {d.label}
                    </span>
                    <span className="text-right">
                      {ints.length
                        ? ints.map((iv, i) => <span key={i} className="block whitespace-nowrap">{fmtInterval(iv)}</span>)
                        : <span className="text-rose-500">Fermé</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </span>
  );
};

export default OpeningStatus;
